import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ChatShareDialog from './ChatShareDialog';
import { fmtTime } from '../../utils/ruDates';

async function copyCodeToClipboard(code) {
    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
        return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = code;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
}

function isTableSeparator(line) {
    return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

function parseTableCells(line) {
    const normalized = line.trim().replace(/^\|/, '').replace(/\|$/, '');
    return normalized.split('|').map((cell) => cell.trim());
}

function inlineFormat(text) {
    if (!text) return text;
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
        if (!part) return null;
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
            return <code key={i} style={{ background: 'rgba(0,0,0,0.08)', borderRadius: '3px', padding: '1px 4px', fontFamily: 'monospace', fontSize: '0.92em' }}>{part.slice(1, -1)}</code>;
        }
        return part;
    });
}

function CodeBlock({ code, language }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = useCallback(() => {
        copyCodeToClipboard(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {});
    }, [code]);

    return (
        <div className="chat-code-block">
            <div className="chat-code-block__header">
                <span className="chat-code-block__lang">{language || 'code'}</span>
                <button type="button" className="chat-code-block__copy" onClick={handleCopy} aria-label="Скопировать код">
                    {copied ? '✓ Скопировано' : 'Копировать'}
                </button>
            </div>
            <pre className="chat-code-block__pre"><code>{code}</code></pre>
        </div>
    );
}

export function renderMarkdown(text) {
    if (!text) return [];
    const lines = text.split('\n');
    const elements = [];
    let key = 0;
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        if (line.startsWith('```')) {
            const language = line.slice(3).trim();
            const codeLines = [];
            i++;
            while (i < lines.length && !lines[i].startsWith('```')) {
                codeLines.push(lines[i]);
                i++;
            }
            elements.push(<CodeBlock key={key++} code={codeLines.join('\n')} language={language} />);
            i++;
            continue;
        }

        if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
            const header = parseTableCells(line);
            const rows = [];
            i += 2;
            while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
                rows.push(parseTableCells(lines[i]));
                i += 1;
            }
            elements.push(
                <div key={key++} className="chat-message__table-wrap">
                    <table className="chat-message__table">
                        <thead>
                            <tr>{header.map((cell, index) => <th key={`h-${index}`}>{inlineFormat(cell)}</th>)}</tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr key={`r-${rowIndex}`}>
                                    {header.map((_, colIndex) => <td key={`c-${rowIndex}-${colIndex}`}>{inlineFormat(row[colIndex] || '')}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
            continue;
        }

        if (line.startsWith('### ')) {
            elements.push(<div key={key++} style={{ fontWeight: 700, marginTop: '8px', marginBottom: '2px' }}>{inlineFormat(line.slice(4))}</div>);
            i++;
            continue;
        }
        if (line.startsWith('## ')) {
            elements.push(<div key={key++} style={{ fontWeight: 700, fontSize: '1.05em', marginTop: '10px', marginBottom: '2px' }}>{inlineFormat(line.slice(3))}</div>);
            i++;
            continue;
        }
        if ((line.startsWith('- ') || line.startsWith('* ')) && line.length > 2) {
            elements.push(<div key={key++} style={{ paddingLeft: '16px', marginBottom: '2px' }}>{'• '}{inlineFormat(line.slice(2))}</div>);
            i++;
            continue;
        }
        const numberedMatch = line.match(/^\d+\. (.+)/);
        if (numberedMatch) {
            elements.push(<div key={key++} style={{ paddingLeft: '16px', marginBottom: '2px' }}>{'• '}{inlineFormat(numberedMatch[1])}</div>);
            i++;
            continue;
        }
        if (line.trim() === '') {
            elements.push(<div key={key++} style={{ height: '6px' }} />);
            i++;
            continue;
        }

        elements.push(<div key={key++}>{inlineFormat(line)}</div>);
        i++;
    }
    return elements;
}

function formatTime(iso) {
    if (!iso) return '';
    try {
        return fmtTime(iso);
    } catch {
        return '';
    }
}

const ChatMessage = React.memo(function ChatMessageInner({ message, onMessageFeedback, requestDislikeComment }) {
    const isAssistant = message.role === 'assistant';
    const markdownContent = useMemo(() => renderMarkdown(message.content || ''), [message.content]);

    return (
        <div className={`chat-message chat-message--${isAssistant ? 'assistant' : 'own'}`} data-testid="chat-message">
            <div className="chat-message__meta">
                <span className="chat-message__author">{isAssistant ? 'AI' : 'Вы'}</span>
                <span className="chat-message__time">{formatTime(message.created_at)}</span>
            </div>
            <div className="chat-message__bubble">
                {message.content && <div className="chat-message__text">{markdownContent}</div>}
                {message.status === 'failed' && (
                    <div className="chat-message__error">
                        {message.provider_error_code || 'provider_error'}: {message.provider_error_message || 'unknown error'}
                    </div>
                )}
                {isAssistant && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            className={`ghost chat-feedback__btn ${message.feedback_vote === 'like' ? 'chat-feedback__btn--active-like' : ''}`}
                            aria-label="Like"
                            title="Полезно"
                            onClick={() => onMessageFeedback?.(message.id, message.feedback_vote === 'like' ? null : 'like')}
                        >
                            👍
                        </button>
                        <button
                            type="button"
                            className={`ghost chat-feedback__btn ${message.feedback_vote === 'dislike' ? 'chat-feedback__btn--active-dislike' : ''}`}
                            aria-label="Dislike"
                            title="Неточно"
                            onClick={() => {
                                if (message.feedback_vote === 'dislike') {
                                    onMessageFeedback?.(message.id, null);
                                    return;
                                }
                                const comment = requestDislikeComment();
                                if (comment === null) return;
                                onMessageFeedback?.(message.id, 'dislike', comment);
                            }}
                        >
                            👎
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});

function ChatThread({
    sessionId,
    sessionTitle,
    busy,
    progressStep,
    messages,
    pendingConfirmation,
    inputValue,
    onInputChange,
    onInputKeyDown,
    onSend,
    onClearMessages,
    onMessageFeedback,
    workspaceSelected,
}) {
    const bottomRef = useRef(null);
    const bodyRef = useRef(null);
    const [shareOpen, setShareOpen] = useState(false);
    const closeShare = useCallback(() => setShareOpen(false), []);

    useEffect(() => {
        const timer = setTimeout(() => {
            const body = bodyRef.current;
            if (body) body.scrollTop = body.scrollHeight;
        }, 0);
        return () => clearTimeout(timer);
    }, [messages]);

    const requestDislikeComment = () => {
        const value = window.prompt('Что не так в этом ответе?');
        if (value === null) return null;
        return String(value).trim();
    };

    return (
        <section className="chat-main">
            <div className="chat-main__head">
                <div>
                    <h3>{sessionTitle || 'AI-чат'}</h3>
                    <p className="muted">Энилекс AI — аналитика, реклама, задачи</p>
                </div>
                <div className="chat-main__head-actions">
                    <button
                        type="button"
                        className="ghost"
                        onClick={() => setShareOpen(true)}
                        disabled={!sessionId || !messages.length}
                        title="Публичная ссылка только для чтения"
                    >
                        Поделиться
                    </button>
                    <button type="button" className="ghost" onClick={onClearMessages} disabled={!messages.length}>
                        Очистить
                    </button>
                </div>
            </div>
            {shareOpen && sessionId && <ChatShareDialog sessionId={sessionId} onClose={closeShare} />}

            <div className="chat-main__body" ref={bodyRef}>
                {pendingConfirmation && (
                    <div className="chat-confirmation-banner">
                        Ожидаю подтверждения действия{pendingConfirmation.tool ? ` («${pendingConfirmation.tool}»)` : ''} — ответьте «да» в чате, чтобы выполнить.
                    </div>
                )}
                {!messages.length && (
                    <div className="chat-empty-state">
                        <div className="chat-empty-state__icon">🤖</div>
                        <h4>Начните диалог с AI</h4>
                        <p>Задайте вопрос о товарах, рекламе, SEO или аналитике.</p>
                        <ul className="chat-empty-state__hints">
                            <li>«Какие у меня продажи за неделю?»</li>
                            <li>«Почему упали заказы?»</li>
                            <li>«Поставь задачу на SEO-оптимизацию карточки»</li>
                        </ul>
                    </div>
                )}
                {messages.map((message) => (
                    <ChatMessage
                        key={message.id}
                        message={message}
                        onMessageFeedback={onMessageFeedback}
                        requestDislikeComment={requestDislikeComment}
                    />
                ))}
                {busy && (
                    <div className="chat-message chat-message--assistant">
                        <div className="chat-message__meta"><span className="chat-message__author">AI</span></div>
                        <div className="chat-message__bubble chat-message__bubble--thinking">
                            {progressStep && <span className="chat-message__thinking-step">{progressStep}</span>}
                            <span className="thinking-dot" /><span className="thinking-dot" /><span className="thinking-dot" />
                        </div>
                    </div>
                )}
                <div ref={bottomRef} />
            </div>

            <form
                className="chat-main__form"
                onSubmit={(event) => { event.preventDefault(); onSend(); }}
            >
                <div className="chat-input-wrap">
                    <textarea
                        rows={1}
                        maxLength={12000}
                        value={inputValue}
                        onChange={onInputChange}
                        onKeyDown={onInputKeyDown}
                        placeholder={workspaceSelected ? 'Спросите что угодно...' : 'Выберите проект'}
                        disabled={!workspaceSelected}
                        data-testid="chat-input"
                    />
                    <span className="chat-main__hint">Enter — отправить, Shift+Enter — новая строка</span>
                </div>
                <button type="submit" disabled={busy || !workspaceSelected || !inputValue.trim()}>Отправить</button>
            </form>
        </section>
    );
}

export default ChatThread;
